"use client";
import React, { useState, useEffect } from "react";
import { Button, Table, Tag, Dropdown, message, Tooltip } from "antd";
import { PlusOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import ConfirmModal from "../../components/modal/ConfirmModal";
import api from "../../api";
import { useToast } from "../../components/toast";
import { formatDateTime } from "../../utils/helper";

const STATUS_COLOR = { DRAFT: "default", PUBLISHED: "success" };
const STATUS_LABEL = { DRAFT: "Ноорог", PUBLISHED: "Нийтлэгдсэн" };

export default function News() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  // id-ийн оронд slug хадгална
  const [deleteModal, setDeleteModal] = useState({ open: false, slug: null });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const toast = useToast();

  const fetchData = async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const res = await api.news_list({ page, limit });
      const result = res.data.data;
      setData(result.data || []);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: result.meta.total,
      }));
    } catch {
      toast("Өгөгдөл татахад алдаа гарлаа", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
    setCreateLoading(true);
    try {
      const res = await api.news_create_draft();
      // id-ийн оронд slug-аар edit хуудас руу шилжинэ
      const slug = res.data.data.slug;
      navigate(`/news/${slug}`);
    } catch {
      toast("Мэдээ үүсгэхэд алдаа гарлаа", "error");
    } finally {
      setCreateLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.news_delete(deleteModal.slug);
      toast("Амжилттай устгагдлаа", "success");
      setDeleteModal({ open: false, slug: null });
      fetchData();
    } catch {
      toast("Устгахад алдаа гарлаа", "error");
    }
  };

  const columns = [
    {
      title: "Онцолсон",
      dataIndex: "featured",
      key: "featured",
      width: 100,
      align: "center",
      render: (featured) =>
        featured ? (
          <Tooltip title="Онцолсон нийтлэл">
            <StarFilled style={{ color: "#FAAD14", fontSize: 16 }} />
          </Tooltip>
        ) : (
          <StarOutlined style={{ color: "#d9d9d9", fontSize: 16 }} />
        ),
      sorter: (a, b) => Number(b.featured) - Number(a.featured),
    },
    {
      title: "Ангилал",
      dataIndex: "taxonomies",
      key: "taxonomies",
      width: 200,
      render: (taxonomies = []) => {
        const cats = taxonomies.filter((t) => t.type === "CATEGORY");
        if (cats.length === 0) return <span>-</span>;

        const MAX_VISIBLE = 1;
        const visible = cats.slice(0, MAX_VISIBLE);
        const rest = cats.slice(MAX_VISIBLE);

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {visible.map((t) => (
              <Tag
                key={t.id}
                style={{
                  fontSize: 12,
                  margin: 0,
                }}
              >
                {t.name}
              </Tag>
            ))}
            {rest.length > 0 && (
              <Tooltip
                title={rest.map((t) => t.name).join(", ")}
                style={{ margin: 0 }}
              >
                <Tag style={{ fontSize: 12, margin: 0 }}>+{rest.length}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Гарчиг",
      dataIndex: "title",
      key: "title",
      width: 500,
      render: (title, record) => (
        <span
          className="two-line-text"
          style={{ cursor: "pointer", fontWeight: 500 }}
          onClick={() => navigate(`/news/${record.slug}`)}
        >
          {title || "-"}
        </span>
      ),
    },

    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Tag>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Нийтлэгч",
      dataIndex: "author",
      key: "author",
      render: (author) => author?.name ?? "—",
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => <div>{formatDateTime(v)}</div>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") navigate(`/news/${record.slug}`);
              if (key === "delete")
                setDeleteModal({ open: true, slug: record.slug });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="news-section">
      <PageHeader
        title="Мэдээ"
        sub="Мэдээ, мэдээллийн жагсаалт"
        rSide={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={createLoading}
            onClick={handleCreate}
          >
            Нийтлэл
          </Button>
        }
      />

      <div className="table-wrap">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={{ spinning: loading, size: "small" }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => `Нийт ${total} мэдээ`,
            onChange: (page, limit) => fetchData(page, limit),
          }}
        />
      </div>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        variant="error"
        title="Итгэлтэй байна уу?"
        description={
          <>
            <span style={{ fontWeight: 600 }}>"Нийтлэл устгах"</span> үйлдлийг
            буцаах боломжгүйг анхаарна уу.
          </>
        }
      />
    </section>
  );
}
